import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import Case from 'case';
import styled from 'styled-components';
import {
  Box, Text, Image, Button,
} from 'grommet';
import {
  Seo, ProductRowStats, InfoNotice, UnderConstruction, SimpleModal, SubmissionLoader,
} from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ListSearchesTablePartial } from '@Components/Partial/Product/ListSearches';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { buildDateLabel } from '@Helpers/util';
import {
  handleQuery,
  handleSort,
  handleFilter,
} from '@Helpers/controlFunctions';
import {
  initiateProductListSearchesRequest,
  clearProductListSearchesRequest,
  initiateProductListSearchesMetaRequest,
  initiateProductListSearchesStatsRequest,
  clearProductListSearchesStatsRequest,
  submitSearchFeatureRequest,
} from '@Actions';

const StyledAddedSearchContainer = styled(Box)`
  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const BannerDisplayContainer = ({
  children, title, tagBg, containerBg,
}) => (
  <Box direction="column" width="100%">
    <Box
      background={tagBg}
      round={{ size: '8px', corner: 'top' }}
      width="fit-content"
      pad={{ horizontal: '0.5rem', vertical: '0.2rem' }}
    >
      <Text color="white" size="0.85rem" weight={600}>{title}</Text>
    </Box>
    <StyledAddedSearchContainer background={containerBg} direction="column" pad="1rem" justify="between">
      {children}
    </StyledAddedSearchContainer>
  </Box>
);

BannerDisplayContainer.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  tagBg: PropTypes.string.isRequired,
  containerBg: PropTypes.string.isRequired,
};

const BannerChildren = ({
  small,
  bgColor,
  addedTagBg,
  summaryTagBg,
  dashboardPath,
  textColor,
  seeDetailsIcon,
  timerIcon,
  focusHighlight,
  buttonHighlight,
  addedSearch = null,
  summaryData = null,
}) => {
  const [showModal, setShowModal] = React.useState(false);

  const renderAddedSearch = ({
    name, startDate, endDate, resultCount,
  }) => (
    <BannerDisplayContainer
      title="Added"
      tagBg={addedTagBg}
      containerBg={bgColor}
    >
      <Box direction="column" gap="0.5rem">
        <Box width="100%" direction="row" justify="between">
          <Text color={textColor} weight={600} size="1rem">
            {name}
          </Text>
          <Box
            direction="row"
            round="12.5px"
            pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
            background="#F0FAF7"
            align="center"
            justify="center"
            gap="0.35rem"
            width="fit-content"
            alignSelf="end"
          >
            <Box flex={false} width="0.9rem" height="0.9rem">
              <Image
                src={timerIcon}
                alt="Recently added search"
                fit="contain"
                width="0.9rem"
                height="0.9rem"
              />
            </Box>
            <Text color="#00945E" weight={600} size="0.9rem">
              Running
            </Text>
          </Box>
        </Box>
        <Text size="1rem" color={textColor}>
          {resultCount ? `${resultCount} infringements found` : '0 infringements found'}
        </Text>
        <Box width="100%" direction="row" justify="between">
          <Text size="1rem" color={textColor}>
            {buildDateLabel(startDate, endDate, true)}
          </Text>
          <Button plain href={dashboardPath(addedSearch.id)}>
            <Box direction="row" align="center" gap="0.5rem">
              <Text size="1rem" color={focusHighlight}>
                See details
              </Text>
              <Box flex={false} width="1rem" height="1rem">
                <Image
                  src={seeDetailsIcon}
                  alt="See more details"
                  fit="contain"
                  width="1rem"
                  height="1rem"
                />
              </Box>
            </Box>
          </Button>
        </Box>
      </Box>
    </BannerDisplayContainer>
  );

  const renderSummaryData = ({ display, value }) => {
    let displayValue = value.join(', ');
    let restValues = null;

    if (value.length >= 10) {
      const firstValues = value.slice(0, 10);
      restValues = value.slice(10);
      displayValue = `${firstValues.join(', ')} + ${restValues.length} more`;
    }

    return (
      <BannerDisplayContainer
        title="Summary"
        tagBg={summaryTagBg}
        containerBg={bgColor}
      >
        <Box direction="row" gap="0.5rem">
          <Text color={textColor} weight={600} size="1rem" style={{ lineHeight: '1.5' }}>
            {display}
            {': '}
            <Text size="1rem" weight="normal" color={textColor}>
              {displayValue}
              {restValues && (
                <Box width="fit-content" pad={{ left: '0.5rem' }} style={{ display: 'inline-block' }}>
                  <Button plain onClick={() => setShowModal(true)}>
                    <Text size="0.9rem" weight={500} color={buttonHighlight}>
                      (View all)
                    </Text>
                  </Button>
                </Box>
              )}
            </Text>
          </Text>
        </Box>
        {showModal && (
          <SimpleModal
            small={small}
            toggleModal={setShowModal}
            modalTitle={display}
            closeLabel="Close"
            highlightColor={buttonHighlight}
            titleSize="1.2rem"
          >
            <Box gap="small" width="100%">
              <Text size="1.2rem" color="var(--page-modal-text)" style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>
                {value.join(', ')}
              </Text>
            </Box>
          </SimpleModal>
        )}
      </BannerDisplayContainer>
    );
  };

  return (
    <Box direction="column" gap="1rem" width={small ? '100%' : '65%'}>
      {addedSearch && renderAddedSearch(addedSearch)}
      {summaryData && summaryData?.value?.length > 0 && renderSummaryData(summaryData)}
    </Box>
  );
};

BannerChildren.propTypes = {
  small: PropTypes.bool.isRequired,
  bgColor: PropTypes.string.isRequired,
  addedTagBg: PropTypes.string.isRequired,
  addedSearch: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    resultCount: PropTypes.number.isRequired,
  }),
  dashboardPath: PropTypes.func.isRequired,
  seeDetailsIcon: PropTypes.string.isRequired,
  timerIcon: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  focusHighlight: PropTypes.string.isRequired,
  buttonHighlight: PropTypes.string.isRequired,
  summaryTagBg: PropTypes.string.isRequired,
  summaryData: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  }),
};

const ListSearchesPage = ({
  small,
  loading,
  submitLoading,
  location,
  fetchListData,
  clearListData,
  fetchListMeta,
  fetchListStats,
  clearListStats,
  submitSearchFeature,
  searchFeatureConfig,
  reviewEnabled,
  paymentPending,
  listSearchesData = null,
  listSearchesMeta = null,
  listSearchesStats = null,
  searchDisabled = false,
}) => {
  const [query, setQuery] = React.useState({
    paging: {
      to: 1,
      size: 10,
    },
  });
  const pageParams = useParams();
  const currentListUid = pageParams.status;
  const scope = currentListUid === 'all' ? null : currentListUid;
  const {
    theme: { page, button },
    disableSearchFeatures,
    constructionImage,
    icons,
    isDarkMode,
  } = useAppTheme();
  const underMaintenance = (disableSearchFeatures || searchDisabled);
  const dashboardPath = (searchId) => paths.productSearchResults.replace(':id', searchId);

  React.useEffect(() => {
    const useQuery = scope ? { ...query, scope } : { ...query };
    fetchListData(useQuery);
    fetchListStats();
    fetchListMeta();

    return () => {
      clearListData();
      clearListStats();
    };
  }, [currentListUid]);

  React.useEffect(() => {
    const useQuery = scope ? { ...query, scope } : { ...query };
    fetchListData(useQuery);
  }, [query]);

  const handleSearchFeature = (id, feature) => {
    let redirectUid = currentListUid;
    switch (feature) {
      case 'pause':
        redirectUid = 'paused';
        break;
      case 'resume':
        redirectUid = 'live';
        break;
      default:
        break;
    }

    const redirectPath = paths.productListSearches.replace(':status', redirectUid);
    submitSearchFeature(id, feature, redirectPath);
  };

  const handleExportCSV = () => {
    const useQuery = scope ? { ...query, scope } : { ...query };
    fetchListData(useQuery, true);
  };

  const pageTitle = `${Case.capital(currentListUid)} searches`;
  const showViewCount = Boolean(searchFeatureConfig?.enabledIndexViewCount);
  const showUnit = Boolean(searchFeatureConfig?.unitTerm);
  let headerMap = [
    {
      display: 'Title',
      uid: 'name',
      size: '29%',
    },
    {
      display: 'Results',
      uid: 'resultCount',
      size: '20%',
    },
    {
      display: 'Date',
      uid: 'date',
      size: '26%',
    },
    {
      display: 'Status',
      uid: 'status',
      size: '25%',
    },
  ];

  if (showUnit) {
    headerMap = [
      {
        display: 'Title',
        uid: 'name',
        size: '24%',
      },
      {
        display: searchFeatureConfig?.unitTerm || 'Unit',
        uid: 'unit',
        size: '12%',
      },
      {
        display: 'Results',
        uid: 'resultCount',
        size: '12%',
      },
      {
        display: 'Date',
        uid: 'date',
        size: '26%',
      },
      {
        display: 'Status',
        uid: 'status',
        size: '25%',
      },
    ];
  }

  // TODO: For the time being, disabling the "Added" banner display until we have a new strategy
  // for starting searches or displaying newly added searches.
  // let addedSearch = null;
  // if (listSearchesAdded?.data?.totalCount === 1) {
  //   const newAdded = listSearchesAdded.data.searches;

  //   if (typeof newAdded === 'object') {
  //     addedSearch = newAdded;
  //   } else if (newAdded && newAdded.length > 0) {
  //     [addedSearch] = newAdded;
  //   }
  // }

  let summaryData = null;
  if (listSearchesData?.data?.summary) {
    [summaryData] = listSearchesData.data.summary;
  }

  const padding = small ? '1.5rem' : '2rem';
  const showBannerChildren = summaryData && summaryData.value?.length > 0; // || addedSearch;
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const resultsPerPageOptions = [10, 15, 20, 25];
  const displayStats = [];

  if (currentListUid === 'live') {
    displayStats.push('running', 'scheduled');
  } else if (currentListUid === 'all') {
    displayStats.push('running', 'scheduled', 'paused', 'completed');
  } else {
    displayStats.push(currentListUid);
  }

  const jobsInProgress = Boolean(listSearchesData?.data?.jobsInProgress);
  const jobsCount = listSearchesData?.data?.jobsCount ?? null;
  const showJobsNotice = jobsInProgress && Boolean(jobsCount && jobsCount > 0);
  let message = 'New searches are currently being processed. All searches can be tracked in the table below once they have been initialized.';
  if (jobsInProgress && jobsCount) {
    message = `Processing ${jobsCount} new search${jobsCount > 1 ? 'es' : ''}. All searches can be tracked in the table below once they have been initialized.`;
  }

  const renderPageContent = () => {
    if (underMaintenance) {
      return (
        <Box flex align="center" justify="center" background="white">
          <UnderConstruction
            imageUrl={constructionImage}
            buttonHighlight={button.primaryBg}
            header="Under Maintenance"
            message="System currently undergoing maintenance. Please check back later."
          />
        </Box>
      );
    }

    return (
      <Box flex background={usePageBg} pad={listSearchesStats ? { top: '0', bottom: padding } : { vertical: padding }}>
        {listSearchesStats && displayStats.length > 0 && (
          <Box width="100%" direction="row" justify="end" pad={{ vertical: '0.65rem', right: '1.5rem' }}>
            {displayStats.map((uid, i) => (
              <ProductRowStats
                key={uid}
                small={small}
                isLast={i === displayStats.length - 1}
                label={`${Case.capital(uid)}:`}
                stat={listSearchesStats[uid]}
                color={page.primaryText}
              />
            ))}
          </Box>
        )}
        {(currentListUid === 'paused') && (
          <InfoNotice
            header="A search can be paused for up to 30 days."
            text="You can resume or delete a search before then. After 30 days, paused searches will be moved to completed."
          />
        )}
        {showJobsNotice && (
          <InfoNotice
            isLoader
            showRefresh
            header="Search creation in progress..."
            text={message}
          />
        )}
        <ListSearchesTablePartial
          small={small}
          listSearchesData={listSearchesData}
          listSearchesMeta={listSearchesMeta}
          handleQuery={(parentKey, updateKey, value) => (
            handleQuery(query, setQuery, parentKey, updateKey, value, {
              paging: {
                to: 1,
                size: query.paging?.size || 10,
              },
            })
          )}
          handleSort={(sortValue, sortDirection) => (
            handleSort(query, setQuery, sortValue, sortDirection)
          )}
          handleFilter={(key, value, predicate, seperateValues, reset) => (
            handleFilter(query, setQuery, key, value, predicate, seperateValues, reset)
          )}
          resultsPerPageOptions={resultsPerPageOptions}
          currentListUid={currentListUid}
          dashboardPath={(searchId) => dashboardPath(searchId)}
          submitSearchFeature={(id, feature) => handleSearchFeature(id, feature)}
          handleExportCSV={handleExportCSV}
          searchQuery={query}
          pageTitle={pageTitle}
          headerMap={headerMap}
          showViewCount={showViewCount}
          showUnit={showUnit}
          reviewEnabled={reviewEnabled}
          loading={loading}
          paymentPending={paymentPending}
        />
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle: 'Results',
        bannerChildren: showBannerChildren && (
          <BannerChildren
            bgColor={page.cardBg}
            small={small}
            textColor={page.primaryText}
            focusHighlight={page.focusHighlight}
            buttonHighlight={button.primaryBg}
            seeDetailsIcon={icons.seeDetailsIcon}
            timerIcon={icons.timerClockIcon}
            addedTagBg={page.numberHighlight}
            addedSearch={null}
            dashboardPath={(searchId) => dashboardPath(searchId)}
            summaryTagBg={isDarkMode ? page.highlight : '#22684B'}
            summaryData={summaryData}
          />
        ),
        bannerChildrenPosition: 'bottom',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.primaryText}
          loaderColor={page.highlight}
          message="Fetching CSV export..."
          contentWidth="20rem"
        />
      )}
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    listSearchesData: state.productList.productListSearches,
    listSearchesMeta: state.productList.productListSearchesMeta,
    listSearchesStats: state.productList.productListSearchesStats,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchListData: initiateProductListSearchesRequest,
    clearListData: clearProductListSearchesRequest,
    fetchListMeta: initiateProductListSearchesMetaRequest,
    fetchListStats: initiateProductListSearchesStatsRequest,
    clearListStats: clearProductListSearchesStatsRequest,
    submitSearchFeature: submitSearchFeatureRequest,
  }, dispatch);
}

ListSearchesPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchListData: PropTypes.func.isRequired,
  clearListData: PropTypes.func.isRequired,
  fetchListMeta: PropTypes.func.isRequired,
  fetchListStats: PropTypes.func.isRequired,
  clearListStats: PropTypes.func.isRequired,
  submitSearchFeature: PropTypes.func.isRequired,
  listSearchesData: PropTypes.shape({
    data: PropTypes.shape({
      totalCount: PropTypes.number.isRequired,
      summary: PropTypes.arrayOf(PropTypes.shape({
        uid: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
        value: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      }).isRequired),
      searches: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string,
        status: PropTypes.string.isRequired,
        priority: PropTypes.string,
        unit: PropTypes.string,
        resultCount: PropTypes.number.isRequired,
        features: PropTypes.arrayOf(PropTypes.string.isRequired),
      })).isRequired,
      jobsCount: PropTypes.number.isRequired,
      jobsInProgress: PropTypes.bool.isRequired,
    }),
  }),
  listSearchesMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validValues: PropTypes.oneOfType([
          PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string.isRequired,
              formatted: PropTypes.string.isRequired,
            }),
          ),
          PropTypes.arrayOf(PropTypes.string),
        ]),
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      }).isRequired).isRequired,
    }).isRequired,
    status: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
      }).isRequired).isRequired,
    }).isRequired,
  }),
  listSearchesStats: PropTypes.shape({
    running: PropTypes.number.isRequired,
    scheduled: PropTypes.number.isRequired,
    completed: PropTypes.number.isRequired,
    paused: PropTypes.number.isRequired,
    canceled: PropTypes.number.isRequired,
  }),
  searchFeatureConfig: PropTypes.shape({
    enabledIndexViewCount: PropTypes.bool,
    unitTerm: PropTypes.string,
  }).isRequired,
  reviewEnabled: PropTypes.bool.isRequired,
  searchDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ListSearchesPage));
